import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Calendar as CalendarIcon } from '../../../icons';
import { formatDate } from '../../../lib/base-i18n';
import { useFormField } from '../FormField';
import { Calendar } from './Calendar';
import { addDays, addMonths, fromDayKey, orderPair, startOfDay, toDayKey } from './calendar-core';
import './DatePicker.css';

/**
 * DateRangePicker — 기간 (디자인 정본 C2).
 * 쓰이는 곳: 휴가 신청 · 출장 · 근태 조회 · 통계 기간 · 예약.
 *
 * ★★★ **지금은 `type="date"` 두 칸**이라 「시작이 끝보다 뒤인지」를 **사용자가 직접 확인**해야 한다.
 *    여기서는 어느 쪽을 먼저 누르든 **작은 것이 시작**이 된다(`orderPair`).
 *
 * ★★★ **끝 날짜가 «배타적»인 API 가 있다** — 예약이 그렇다(`endAt` 이 다음 날 00:00).
 *    그런 값을 그대로 칠하면 **하루가 더 칠해진다.** `endExclusive` 를 켜면
 *    받을 때 하루 되돌리고 내보낼 때 하루 더한다 — 사용자가 보는 기간과 저장되는 값이 어긋나지 않게.
 *
 * ★ 두 달을 나란히 놓는다 — 달을 넘나들며 고르는 일이 잦아서다(월말~월초 휴가).
 */
export interface DateRange {
  /** `YYYY-MM-DD` */
  start: string | null;
  /** `YYYY-MM-DD` — `endExclusive` 면 **보이는 마지막 날의 다음 날**이 오간다. */
  end: string | null;
}

export interface DateRangePickerProps {
  value: DateRange;
  onChange: (next: DateRange) => void;
  min?: string | null;
  max?: string | null;
  disabledDays?: ReadonlySet<string>;
  holidays?: ReadonlySet<string>;
  /** 끝 날짜가 배타적인 API 를 쓸 때. 기본은 포함(사용자가 보는 대로). */
  endExclusive?: boolean;
  /** 자주 쓰는 기간 단추를 보인다. */
  presets?: boolean;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  weekStart?: 0 | 1;
  ariaLabel?: string;
  className?: string;
}

/** 화면이 보여 주는 마지막 날 ↔ API 가 주고받는 끝 값. */
const toShownEnd = (end: string | null, exclusive: boolean): Date | null => {
  const d = end ? fromDayKey(end) : null;
  return d && exclusive ? addDays(d, -1) : d;
};
const toApiEnd = (shown: Date, exclusive: boolean): string => toDayKey(exclusive ? addDays(shown, 1) : shown);

export function DateRangePicker({
  value,
  onChange,
  min,
  max,
  disabledDays,
  holidays,
  endExclusive = false,
  presets = true,
  placeholder = '기간을 고르세요',
  disabled,
  invalid,
  weekStart = 0,
  ariaLabel,
  className,
}: DateRangePickerProps) {
  const field = useFormField();
  const uid = useId();
  const popId = `${uid}-pop`;
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState<Date | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const startDate = value.start ? fromDayKey(value.start) : null;
  const endShown = toShownEnd(value.end, endExclusive);
  const [month, setMonth] = useState<Date>(() => startDate ?? new Date());

  useEffect(() => { if (startDate) setMonth(startDate); }, [value.start]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (!rootRef.current?.contains(e.target as Node)) setOpen(false); };
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onEsc, true);
    };
  }, [open]);

  const selected = useMemo(
    () => [startDate, endShown].filter((d): d is Date => d != null),
    [value.start, value.end, endExclusive],
  );

  const pick = (d: Date) => {
    // 시작만 있거나 아무것도 없으면 시작을 새로 잡는다. 둘 다 있으면 다시 시작이다.
    if (!startDate || (startDate && endShown)) {
      onChange({ start: toDayKey(d), end: null });
      setHovered(null);
      return;
    }
    const [a, b] = orderPair(startDate, d);   // ★ 어느 쪽을 먼저 눌러도 작은 것이 시작
    onChange({ start: toDayKey(a), end: toApiEnd(b, endExclusive) });
    setHovered(null);
    setOpen(false);
  };

  const applyPreset = (from: Date, to: Date) => {
    onChange({ start: toDayKey(from), end: toApiEnd(to, endExclusive) });
    setMonth(from);
    setOpen(false);
  };

  const today = startOfDay(new Date());
  const PRESETS: Array<[string, () => void]> = [
    ['오늘', () => applyPreset(today, today)],
    ['이번 주', () => {
      const from = addDays(today, -((today.getDay() - weekStart + 7) % 7));
      applyPreset(from, addDays(from, 6));
    }],
    ['이번 달', () => {
      const from = new Date(today.getFullYear(), today.getMonth(), 1);
      applyPreset(from, addDays(addMonths(from, 1), -1));
    }],
    ['지난 달', () => {
      const from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      applyPreset(from, addDays(addMonths(from, 1), -1));
    }],
    ['최근 30일', () => applyPreset(addDays(today, -29), today)],
    ['올해', () => applyPreset(new Date(today.getFullYear(), 0, 1), new Date(today.getFullYear(), 11, 31))],
  ];

  const isDisabled = disabled ?? field?.disabled ?? false;
  const isInvalid = invalid ?? field?.invalid ?? false;
  const label = startDate
    ? `${formatDate(startDate)} — ${endShown ? formatDate(endShown) : '…'}`
    : placeholder;

  return (
    <div
      ref={rootRef}
      className={['wc-datepicker', 'wc-datepicker--range', isInvalid ? 'wc-datepicker--invalid' : '', className ?? '']
        .filter(Boolean).join(' ')}
    >
      <button
        type="button"
        id={field?.controlId}
        className="wc-datepicker__trigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? popId : undefined}
        aria-describedby={field?.describedBy}
        aria-invalid={isInvalid || undefined}
        aria-required={field?.required || undefined}
        aria-label={field?.controlId ? undefined : (ariaLabel ?? placeholder)}
        disabled={isDisabled}
        onClick={() => setOpen((v) => !v)}
      >
        <CalendarIcon className="wc-datepicker__icon" aria-hidden />
        <span className={startDate ? 'wc-datepicker__value' : 'wc-datepicker__placeholder'}>{label}</span>
      </button>

      {open && (
        <div className="wc-datepicker__pop wc-datepicker__pop--range" id={popId} role="dialog" aria-label="기간 고르기">
          {presets && (
            <div className="wc-datepicker__presets">
              {PRESETS.map(([name, fn]) => (
                <button key={name} type="button" className="wc-datepicker__preset" onClick={fn}>{name}</button>
              ))}
            </div>
          )}
          <div className="wc-datepicker__months" onMouseLeave={() => setHovered(null)}>
            {[0, 1].map((i) => (
              <Calendar
                key={i}
                month={i === 0 ? month : addMonths(month, 1)}
                onMonthChange={(next) => setMonth(i === 0 ? next : addMonths(next, -1))}
                selected={selected}
                hovered={hovered}
                onHover={setHovered}
                onSelect={pick}
                min={min ? fromDayKey(min) : null}
                max={max ? fromDayKey(max) : null}
                disabledDays={disabledDays}
                holidays={holidays}
                weekStart={weekStart}
                hideNav={i === 0 ? 'next' : 'prev'}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
